import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, DollarSign, Target, Shield } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function LifeCoachAIAlternativePage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Life Coach AI Alternative - VZMO | Affordable AI Life Coaching';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Looking for a Life Coach AI alternative? VZMO offers transparent pricing, private journaling, and structured coaching. More affordable with better features. Try free for 7 days.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/compare/lifecoachai-alternative');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/compare/lifecoachai-alternative';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('lifecoachai-comparison');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Better Alternative to Life Coach AI
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Get transparent pricing, privacy-first coaching, and structured action plans
            at a fraction of the cost. No hidden fees, no recurring charges you don't expect.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">VZMO</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Life Coach AI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">AI Coaching Conversations</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Transparent Pricing</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Private Journaling</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Structured Action Plans</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><span className="text-xs text-slate-500">Basic</span></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Career Coaching Tools</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Weekly Progress Tracking</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Conversation History</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><span className="text-xs text-slate-500">Limited</span></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Export All Data</td>
                  <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Monthly Price</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">$7/month</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">$29/month</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">Free Trial</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-[#E07A5F]">7 days</td>
                  <td className="px-6 py-4 text-center text-sm text-slate-600">None</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <DollarSign className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">4x More Affordable</h3>
            <p className="text-slate-600">
              Get premium AI coaching for $7/month vs $29/month. Save $264/year without sacrificing quality.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <Shield className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">True Privacy</h3>
            <p className="text-slate-600">
              End-to-end encryption, anonymous usage, and full data export. Your conversations stay yours.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <Target className="w-12 h-12 text-[#E07A5F] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-3">Structured Progress</h3>
            <p className="text-slate-600">
              Move beyond conversations with action plans, milestones, and weekly accountability check-ins.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white mb-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Save $264 Per Year</h2>
            <p className="text-lg mb-6 opacity-90">
              Get better features at a fraction of the cost. Start your free 7-day trial today.
            </p>
            <button
              onClick={handleStartFree}
              className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
            >
              Start Free Trial
            </button>
            <p className="mt-4 text-sm opacity-75">No credit card required</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Common Questions</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Why is VZMO so much cheaper than Life Coach AI?
              </h3>
              <p className="text-slate-600">
                We believe AI coaching should be accessible to everyone. We've built efficient systems
                and kept our pricing honest. No marketing gimmicks, no hidden fees - just straightforward
                value at a fair price.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Is the AI coaching quality lower because it costs less?
              </h3>
              <p className="text-slate-600">
                Not at all. We use the same advanced AI models but we've optimized our infrastructure and
                don't mark up our prices 4x. Our users consistently rate their coaching sessions as helpful
                and insightful.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Can I try VZMO before paying?
              </h3>
              <p className="text-slate-600">
                Yes! We offer a full 7-day free trial with no credit card required. Experience everything
                VZMO offers before making any commitment.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                What if I'm already paying for Life Coach AI?
              </h3>
              <p className="text-slate-600">
                You can easily switch! Start your VZMO trial, and if you like it, simply cancel your
                Life Coach AI subscription. There's no overlap or double charging.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Does VZMO have the same features as Life Coach AI?
              </h3>
              <p className="text-slate-600">
                VZMO has all the core features plus additional tools Life Coach AI doesn't offer: structured
                journaling, career clarity frameworks, weekly progress reports, and full data export capabilities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
